import { useRef, type ReactNode } from 'react';
import { Icon } from './Icon';
import { useDialogA11y } from './useDialogA11y';

/**
 * The dialog shell every ADD / PROPOSE / ASSIGN form now opens in.
 *
 * **Why a dialog rather than the inline panels these forms used to be.** A create form
 * rendered permanently above the list it writes into costs the list its vertical space on
 * every visit, for a control most visits never touch — the Sites page spent its first
 * ~170px on "Add a Site" before showing a single vessel. It also blurs what the page IS:
 * a directory whose first element is a form reads as a form with a directory attached.
 * Moving creation behind a button restores the list as the subject and gives the form room
 * to breathe when it is actually wanted.
 *
 * **Distinct from {@link ConfirmModal}, deliberately not merged with it.** That component
 * asks a yes/no question and owns both its buttons; this one hosts arbitrary form content
 * and owns NEITHER — the caller's `<form>` keeps its own submit button, its own validation
 * and its own in-flight state, because those differ per form and are already tested. The
 * two share the a11y mechanics via `useDialogA11y` (scrim click closes, Esc closes, focus
 * moves to the panel on open and returns to the opener on close, Tab is trapped) rather
 * than sharing a component that would have to be all things to both.
 *
 * **Width is content-sized.** `reconcile-prototype.md` (#17) records that DESIGN.md's
 * single 600px modal under-describes the real family: the prototype ranges ~440–600 by
 * content, 600 being the form default and ~440 the compact/confirm end. `wide` selects the
 * 600px form default; the base is 520, which suits the three- and four-field forms that
 * make up most of these.
 *
 * The dialog is labelled by its heading through `aria-labelledby` rather than `aria-label`,
 * so the accessible name and the visible title are one string and cannot drift.
 */
export function FormModal({
  title,
  titleId,
  children,
  onClose,
  wide = false,
}: {
  /** Visible heading, and the dialog's accessible name. */
  title: string;
  /**
   * DOM id for the heading. Required rather than generated because several of these forms
   * already own an `aria-labelledby` contract with the section they replaced, and the tests
   * that assert it should keep passing unchanged.
   */
  titleId: string;
  /** The form. Keeps its own submit control, validation and busy state. */
  children: ReactNode;
  /** Dismiss — wired to the scrim, Esc and the close button alike. */
  onClose: () => void;
  /** Use the 600px form width instead of the 520px default (see above). */
  wide?: boolean;
}): React.JSX.Element {
  const panelRef = useRef<HTMLElement>(null);
  useDialogA11y(panelRef, onClose);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 'var(--z-modal)' as unknown as number,
        overflowY: 'auto',
      }}
    >
      <section
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className="card stack"
        style={{
          width: `min(${wide ? 600 : 520}px, 100%)`,
          // A tall form (the propose-device form with its inline-vendor arm expanded) must
          // scroll INSIDE the panel rather than pushing the dialog off-screen, which on a
          // laptop viewport would put the submit button somewhere unreachable.
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          boxShadow: 'var(--elevation-modal)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
            {title}
          </h2>
          {/* Labelled on the BUTTON, not the glyph — the icon stays aria-hidden so the
              control announces once. */}
          <button type="button" className="icon-button" data-icon-only="true" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}
